import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import * as analytics from 'analytics/Analytics';
import * as BenchlingUtil from 'BenchlingUtil';
import {WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeSimpleActionFunction from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {
  isProcessTaskIdsContext,
  OutsideNotebookContext,
} from 'notebook/structured-tables/StructuredTableOutsideNotebookConstants';
import tableHasUnsubmittedChanges from 'notebook/structured-tables/util/tableHasUnsubmittedChanges';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
import notifications from 'notifications';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';

import {
  FetchProcessOutputsLineageEdges,
  FetchProcessOutputsLineageEdgesVariables,
  FetchProcessOutputsLineageEdges_processOutputs_ProcessOutputNode_lineageEdgesFrom_ProcessLineageEdgeNode_toVertex_ProcessTaskNode_process_ProcessNode as ProcessNodeFromVertex,
} from './__generated/FetchProcessOutputsLineageEdges';

export const FETCH_OUTPUTS_LINEAGE_EDGES_QUERY: DocumentNode = gql`
  query FetchProcessOutputsLineageEdges($outputIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    processOutputs(ids: $outputIds) {
      ... on ProcessOutputNode {
        id
        lineageEdgesFrom {
          id
          toVertex {
            ... on ProcessTaskNode {
              id
              process {
                id
                editUrl
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export async function fetchLiveProcessOutputMetadata(
  structuredTable: ProcessOutputStructuredTable
): Promise<Array<ProcessNodeFromVertex>> {
  const outputIdsFromTable = [];
  for (let i = 0; i < structuredTable.rows.length; i++) {
    for (let j = 0; j < structuredTable.rows[i].cells.length; j++) {
      const typedValue = structuredTable.rows[i].cells[j].typedValue;
      if (typedValue?.type === BaseTypedValueType.PROCESS_OUTPUT) {
        outputIdsFromTable.push(typedValue.id);
        break;
      }
    }
  }

  const queryResult = await doQuerySafe<
    FetchProcessOutputsLineageEdges,
    FetchProcessOutputsLineageEdgesVariables
  >(
    {query: FETCH_OUTPUTS_LINEAGE_EDGES_QUERY},
    {variables: {outputIds: outputIdsFromTable}, fetchPolicy: 'network-only'}
  );

  const nextGroups = queryResult.processOutputs.flatMap((output) => {
    if (!isInaccessible(output)) {
      return output.lineageEdgesFrom.flatMap((edge) => {
        if (edge.toVertex.__typename === 'ProcessTaskNode') {
          return edge.toVertex.process;
        }
        return [];
      });
    }
    return [];
  });
  return nextGroups;
}

async function openNextTaskGroup(
  structuredTable: ProcessOutputStructuredTable,
  outsideNotebookContext?: OutsideNotebookContext | null
): Promise<void> {
  const nextGroups = await fetchLiveProcessOutputMetadata(structuredTable);
  if (nextGroups.length < 1) {
    notifications.warn(
      "The next task isn't available. It either hasn't been created yet, or you've reached the final task. If this isn't the final task, please try again later."
    );
  } else {
    // If more than one downstream task group exists, arbitrarily pick one
    // TODO: improve heuristic or give users choice on task group to jump to
    BenchlingUtil.navigate(null, nextGroups[0].editUrl);
  }
  analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_NEXT_TASK_GROUP_BUTTON, {
    action_method: ActionMethod.BUTTON_CLICK,
    next_groups_count: nextGroups.length,
    action_origin:
      outsideNotebookContext != null && isProcessTaskIdsContext(outsideNotebookContext)
        ? ActionOrigin.WORKFLOWS_TASK_GROUP_WORKFLOW_OUTPUT_TABLE
        : ActionOrigin.NOTEBOOK_ENTRY_WORKFLOW_OUTPUT_TABLE,
  });
}

export const openNextTaskGroupActionFunction = makeSimpleActionFunction({
  label: 'Open next task group',
  icon: ModelTypeConstants.PROCESS_TASK,
  onInvoke: ({
    structuredTable,
    outsideNotebookContext,
  }: {
    structuredTable: ProcessOutputStructuredTable;
    outsideNotebookContext?: OutsideNotebookContext | null;
  }) => {
    openNextTaskGroup(structuredTable, outsideNotebookContext);
  },
  getDisabled: ({structuredTable, computedTable}) =>
    tableHasUnsubmittedChanges(structuredTable, computedTable),
});
